import { WebRTC2, debugerNIMEventList } from '@/utils/NIM/index';
import { NIMEventLog } from '@/utils/sentry';
import { NetworkQuality } from '@/types';

export interface Stream {
  getId: () => number;
  [k: string]: any;
}

interface Options {
  appKey: string;
  clientKey?: string;
  stopScreenSharing?: () => void;
  notAllowedError?: () => void;
  onerror?: () => void;
  connectionStateChange?: (evt: ConnectionStateChangeEvt) => void;
}

interface ConnectionStateChangeEvt {
  curState: 'DISCONNECTED' | 'CONNECTED' | 'CONNECTING';
  prevState: 'DISCONNECTED' | 'CONNECTED' | 'CONNECTING';
}

interface JoinListenerOptions {
  peerOnline?: (yxuid: string) => void;
  peerLeave?: (yxuid: string) => void;
  addStream?: (stream: Stream) => void;
  removeStream?: (stream: Stream) => void;
  muteAudio?: (yxuid: string) => void;
  unMuteAudio?: (yxuid: string) => void;
  activeSpeaker?: (data: Record<string, any>) => void;
  audioTrackEnded?: () => void;
  videoTrackEnded?: () => void;
  volumeIndicator?: (data: any) => void;
  networkQuality?: (data: NetworkQuality[]) => void;
  clientBanned?: (yxuid: string) => void;
  clientClosed?: () => void;
}

interface JoinChannelOptions {
  channelName: string;
  yxuid: string;
  token: string;
}

type JoinOptions = JoinListenerOptions & JoinChannelOptions;

let mainClient: null | any = null;

// 创建主客户端
const initMainClient = async (options: Options) => {
  //  clientKey = 'SIMULTANEOUS_INTERPRETING',
  const { appKey, onerror, connectionStateChange, notAllowedError, stopScreenSharing } = options;
  // 这里只是创建一个会话客户端， 之后需要加入到一个房间才能和远端通信
  const client = await WebRTC2.createClient({
    appkey: appKey, // 您的 appkey
    debug: process.env.NODE_ENV === 'development' ? false : true, // 是否开启调试日志
    report: true,
  });

  mainClient = client;
  WebRTC2.Logger?.enableLogUpload?.(true);
  console.log(mainClient, 'mainclient');

  // (本地流)
  // 表示获取设备权限被拒绝;
  client.on('accessDenied', (type: string) => {
    console.warn('==== %s设备开启的权限被禁止了', type);
  });
  // 停止屏幕共享 在屏幕共享浮窗上点击关闭触发(浏览器上的按钮、不是自定义的按钮)
  client.on('stopScreenSharing', (evt: any) => {
    console.warn('触发stopScreenSharing事件', evt);
    stopScreenSharing && stopScreenSharing();
  });

  // 检测是否触发了浏览器自动播放策略(一般只针对远端流)
  // https://doc.yunxin.163.com/nertc/docs/jM3NDE0NTI?platform=web
  client.on('NotAllowedError', (error: any) => {
    console.log(error, 'NotAllowedError');
    const errorCode = error?.getCode?.() || error?.error?.getCode?.() || error?.error?.code;
    if (errorCode === 41030) {
      notAllowedError?.();
    }
  });

  // =================================
  // （全局）
  // 客户端发生错误
  client.on('error', async (evt: any) => {
    console.log('云信-error', evt);
    if (evt === 'SOCKET_ERROR') {
      onerror && onerror();
    }
  });

  // SDK 与服务器的连接状态发生了变化
  client.on('connection-state-change', (evt: any) => {
    connectionStateChange && connectionStateChange(evt);
  });

  // 各类型错误上报到 elk
  debugerNIMEventList.forEach((eventStr) => {
    client.on(eventStr, (evt: any) => {
      const tagObj = { eventType: eventStr };
      NIMEventLog(tagObj, { data: evt, ...tagObj });
    });
  });
  // client.on('active-speaker', (data: any) => {});
  return client;
};

// 加入房间的监听函数
const joinListener = (options: JoinListenerOptions) => {
  console.log(mainClient, options, 'options');
  if (!mainClient) {
    throw new Error('当前 client 实例不存在！');
  }
  const {
    peerOnline,
    peerLeave,
    muteAudio,
    unMuteAudio,
    networkQuality,
    addStream,
    removeStream,
    activeSpeaker,
    volumeIndicator,
    clientClosed,
    clientBanned,
    audioTrackEnded,
    videoTrackEnded,
  } = options;
  // 以下回调在 client 创建即可监听，但是需要 join 之后，才会有相关推送
  // (远端流)
  // 远端用户加入通话房间的事件
  mainClient.on('peer-online', (evt: { uid: string }) => {
    console.log('peer-online =============');
    peerOnline && peerOnline(evt.uid);
  });

  // 远端用户或主播角色离开房间;
  mainClient.on('peer-leave', (evt: { uid: string }) => {
    console.log('peer-leave ================');
    const yxUid = evt.uid;
    peerLeave && peerLeave(yxUid);
  });

  // 远端用户发布了音视频流 即这个人调用了 publish 接口
  // 可以通过 subscribe 进行订阅
  mainClient.on('stream-added', async (evt: any) => {
    console.log('stream-added ===============');
    addStream && addStream(evt);
  });

  // 远端用户停止发布音视频流 即这个人调用了 unpublish 接口
  mainClient.on('stream-removed', async (evt: any) => {
    console.log('stream-removed ===============');
    const { stream } = evt;
    stream && (await stream.stop(evt?.mediaType));
    removeStream?.(stream);
  });

  // ====== mute-audio 和 unmute-audio 按目前的业务 貌似都不会触发 =====
  // A 调用 muteAudio mute-audio 才会触发，通过系统调节音量或者通过 open/close 操作麦克风 是不会触发 mute-audio 的
  // 远端用户静音其音频，即关掉自己的声音
  mainClient.on('mute-audio', (evt: { uid: string }) => {
    console.log('mute-audio ===============');
    const yxUid = evt.uid;
    // 上报检测一下该功能是否被使用
    const tagObj = { eventType: 'mute-audio' };
    NIMEventLog(tagObj, { data: evt, ...tagObj });
    muteAudio && muteAudio(yxUid);
  });

  // 远端用户取消静音，即打开自己的声音
  mainClient.on('unmute-audio', (evt: { uid: string }) => {
    console.log('unmute-audio ===============');
    const yxUid = evt.uid;
    // 上报检测一下该功能是否被使用
    const tagObj = { eventType: 'unmute-audio' };
    NIMEventLog(tagObj, { data: evt, ...tagObj });
    unMuteAudio && unMuteAudio(yxUid);
  });

  // ==== end ====

  // 当前说话的人
  mainClient.on('active-speaker', (data: any) => {
    console.warn('=============== 当前在频道讲话的人：', data.uid);
    activeSpeaker?.(data);
  });

  // 当前说话的人
  mainClient.on('volume-indicator', (data: any) => {
    console.warn('=============== 远程音量回调：', data);
    volumeIndicator?.(data);
  });

  // 展示房间中所有成员的上下行网络质量
  mainClient.on('network-quality', (data: NetworkQuality[]) => {
    // console.log('network-quality ============');
    networkQuality && networkQuality(data);
    const badNetworkQuality = data.find(
      (n) => n.downlinkNetworkQuality >= 3 || n.uplinkNetworkQuality >= 3
    );
    if (badNetworkQuality) {
      const tagObj = { eventType: 'network-quality', badNetworkQuality };
      NIMEventLog(tagObj, { data, eventType: 'network-quality' });
    }
  });

  // 房间被关闭
  mainClient.on('channel-closed', () => {
    console.log('channel-closed: 房间被关闭 !!!');
    clientClosed && clientClosed();
  });

  // 监听当前是否有人被踢出房间
  mainClient.on('client-banned', (data: any) => {
    const yxuid = data.uid;
    console.warn(`===== ${data.uid}被提出房间`, yxuid);

    // 目前踢人没有走云信的逻辑，上报日志确认是否可以删除
    const tagObj = { eventType: 'client-banned' };
    NIMEventLog(tagObj, { data, ...tagObj });
    clientBanned && clientBanned(yxuid);
  });

  //表示音频轨道结束。造成的原因可能是设备被拔出
  mainClient.on('audioTrackEnded', () => {
    console.log('audioTrackEnded: 音频设备被拔出');
    audioTrackEnded && audioTrackEnded();
  });
  mainClient.on('videoTrackEnded', (evt: any) => {
    console.log('audioTrackEnded: 视频设备被拔出', evt);
    videoTrackEnded && videoTrackEnded();
  });
};
const joinChannel = async (options: JoinOptions) => {
  const { channelName, yxuid, token } = options;
  if (!mainClient) {
    throw new Error('当前 client 实例不存在！');
  }
  try {
    await mainClient.join({
      channelName,
      uid: yxuid,
      token,
    });
  } catch (e) {
    console.log('join 失败', e);
    await new Promise((resolve) => {
      setTimeout(() => {
        mainClient
          .join({
            channelName,
            uid: yxuid,
            token,
          })
          .catch((err: any) => {
            console.error(err);
          })
          .finally(() => {
            resolve('');
          });
      }, 300);
    });
  }
};

const join = async (options: JoinOptions) => {
  const { channelName, yxuid, token, ...rest } = options;
  joinListener({ ...rest });
  await joinChannel({
    channelName,
    yxuid,
    token,
  });
};

export { initMainClient, join };
